import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useUserStore } from 'store';

import {
  CARD_SX,
  Caption,
  KIT_DIALOG_ACTIONS_SX,
  KIT_DIALOG_TITLE_SX,
  KitHeader,
  Overline,
  TONE,
  TagChip,
} from '../shared/designKit';
import { numFont, plateFont } from '../../utils/fonts';
import { channelShortTranslationKey, skipReasonTranslationKey, triggerLabel } from './reminderUtils';

const HEAD_SX = {
  fontWeight: 600, fontSize: 12, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.5px',
  whiteSpace: 'nowrap', bgcolor: 'background.default',
};

// Dry-run audience list: "Tämä sääntö koskisi juuri nyt N asiakasta". This is
// the activation gate — shown before a rule can be switched on, so an
// accidental blast to 800 customers is impossible.
export default function ReminderPreviewDialog({ open, onClose, rule, catalog, activateAfter, onActivate }) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const orgId = useUserStore((s) => s.currentOrganization?.organization_id);

  const [result, setResult] = useState(null);
  const [error, setError] = useState(false);
  const [activating, setActivating] = useState(false);

  const fetchPreview = useCallback(async () => {
    setResult(null);
    setError(false);
    try {
      const response = await axios.post(
        '/api/reminder-rules/preview/',
        {
          trigger_key: rule.trigger_key,
          interval_months: rule.interval_months,
          channel: rule.channel,
          rule_id: rule.id || null,
          organization: orgId,
        },
      );
      setResult(response.data);
    } catch (err) {
      console.error('Failed to load reminder preview:', err);
      setError(true);
    }
  }, [rule, orgId]);

  useEffect(() => {
    if (open) {
      fetchPreview();
    }
  }, [open, fetchPreview]);

  const handleActivate = async () => {
    setActivating(true);
    try {
      await onActivate();
    } finally {
      setActivating(false);
    }
  };

  const wouldSend = (result?.entries || []).filter((entry) => !entry.skip_reason);
  const skipped = (result?.entries || []).filter((entry) => entry.skip_reason);
  const subtitle = [
    rule.name,
    `${triggerLabel(catalog, rule.trigger_key, i18n.language)} · ${t('service_reminders.months_value', { months: rule.interval_months })}`,
    t(channelShortTranslationKey(rule.channel)),
  ].filter(Boolean).join(' · ');

  const dateCell = (value) => (value ? format(parseISO(value), 'd.M.yyyy') : '');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth fullScreen={fullScreen} data-testid="reminder-preview-dialog">
      <DialogTitle sx={KIT_DIALOG_TITLE_SX}>
        <KitHeader
          icon={<GroupsOutlinedIcon />}
          title={t('service_reminders.preview_title')}
          subtitle={subtitle}
          onClose={onClose}
        />
      </DialogTitle>
      <DialogContent sx={{ p: 0, bgcolor: 'background.default' }}>
        {error && <Alert severity="error" sx={{ m: 3 }}>{t('service_reminders.load_error')}</Alert>}
        {!error && !result && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}
        {result && (
          <Box sx={{ px: { xs: 2, md: 3.5 }, py: { xs: 2.5, md: 3 }, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2, flexWrap: 'wrap' }}>
              <Box sx={{ flex: '1 1 240px' }}>
                <Overline sx={{ mb: 1 }}>{t('service_reminders.preview_within_30_days')}</Overline>
                <Typography
                  data-testid="preview-count"
                  sx={{ fontSize: { xs: 34, md: 40 }, fontWeight: 700, letterSpacing: '-1px', lineHeight: 1, whiteSpace: 'nowrap', ...numFont }}
                >
                  {t('service_reminders.preview_customers', { count: result.count })}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', pb: 0.5 }}>
                <TagChip label={t('service_reminders.preview_would_send_count', { n: wouldSend.length })} tone={TONE.green} />
                {skipped.length > 0 && (
                  <TagChip label={t('service_reminders.preview_skipped_count', { n: skipped.length })} tone={TONE.slate} />
                )}
              </Box>
            </Box>

            {result.truncated && <Caption>{t('service_reminders.preview_truncated')}</Caption>}
            {result.count === 0 && skipped.length === 0 && <Caption>{t('service_reminders.preview_empty_hint')}</Caption>}

            {(wouldSend.length > 0 || skipped.length > 0) && (
              <TableContainer sx={{ ...CARD_SX, maxHeight: 440, overflowX: 'auto' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={HEAD_SX}>{t('service_reminders.col_customer')}</TableCell>
                      <TableCell sx={HEAD_SX}>{t('service_reminders.col_vehicle')}</TableCell>
                      <TableCell sx={HEAD_SX}>{t('service_reminders.col_anchor_date')}</TableCell>
                      <TableCell sx={HEAD_SX}>{t('service_reminders.col_due_date')}</TableCell>
                      <TableCell sx={HEAD_SX}>{t('service_reminders.col_channel')}</TableCell>
                      <TableCell sx={HEAD_SX}>{t('service_reminders.col_status')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[...wouldSend, ...skipped].map((entry) => (
                      <TableRow key={`${entry.rule_id}-${entry.maintenance_id}`} sx={{ '& td': { py: 1.25 } }}>
                        <TableCell sx={{ fontWeight: 600, color: entry.skip_reason ? 'text.secondary' : 'text.primary' }}>
                          {entry.customer_name}
                        </TableCell>
                        <TableCell sx={{ ...plateFont, fontWeight: 600, whiteSpace: 'nowrap' }}>{entry.vehicle_registration}</TableCell>
                        <TableCell sx={{ ...numFont, whiteSpace: 'nowrap', color: 'text.secondary' }}>{dateCell(entry.anchor_completed_at)}</TableCell>
                        <TableCell sx={{ ...numFont, whiteSpace: 'nowrap' }}>{dateCell(entry.due_date)}</TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{entry.channel ? t(channelShortTranslationKey(entry.channel)) : ''}</TableCell>
                        <TableCell>
                          {entry.skip_reason ? (
                            <TagChip label={t(skipReasonTranslationKey(entry.skip_reason))} tone={TONE.slate} />
                          ) : (
                            <TagChip label={t('service_reminders.would_send')} tone={TONE.green} />
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={KIT_DIALOG_ACTIONS_SX}>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose} sx={{ textTransform: 'none', color: 'text.secondary' }}>{t('Close')}</Button>
        {activateAfter && result && (
          <Button
            variant="contained"
            color="secondary"
            onClick={handleActivate}
            disabled={activating}
            data-testid="activate-rule-button"
            sx={{ textTransform: 'none', fontWeight: 600, boxShadow: 'none' }}
          >
            {activating ? <CircularProgress size={20} color="inherit" /> : t('service_reminders.activate_rule', { n: result.count })}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
